import { CustomTooltip, TableAbstract } from "shared/components/Components";
import { Link, useMercuryContext } from "../../context";
import { BlockOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import TimezoneConverter from "components/timezone-converter";
import useBreakpoint from "antd/es/grid/hooks/useBreakpoint";
import { descriptionColumn } from "shared/table/columnsConfig";
import InvoiceTypeForm from "./InvoiceTypeForm";
import { SwitchFilter } from "stories/filters/switch-filter/SwitchFilter";
import { Space } from "antd";
import PermissionWrapper from "shared/components/PermissionWrapper";
import { checkPermissions } from "common/utils/checkPermissions";
import { ROUTES } from "common/constants/routes";

class InvoiceTypes extends TableAbstract<any> {
  defaultSortField = "name";
  FilterComponents = [
    {
      id: "includeDeleted",
      name: "deleted",
      title: "Deleted",
      component: SwitchFilter,
      active: true,
    },
  ];

  columns = [
    {
      id: "name",
      title: "Name",
      dataIndex: "name",
      sorter: true,
      fixed: undefined,
      width: 160,
      render: (id: string, record: any) => {
        return (
          <>
            <a
              href={`${ROUTES.MANAGE_INVOICE_TYPES}/${record?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              title="View invoice type details in a new tab"
            >
              <BlockOutlined style={{ marginRight: "3px" }} />
            </a>
            <CustomTooltip text={id}>
              <Link
                to={`${ROUTES.MANAGE_INVOICE_TYPES}/${record?.id}`}
                id={record?.id}
                title="View invoice type details"
              >
                {id}
              </Link>
            </CustomTooltip>
          </>
        );
      },
    },
    descriptionColumn,
    {
      id: "priority",
      title: "Priority",
      dataIndex: "priority",
      sorter: true,
      fixed: undefined,
      width: 100,
    },
    {
      id: "isFallback",
      title: "Is Fallback",
      dataIndex: "isFallback",
      width: 100,
      fixed: undefined,
      render: (text: string, record: any) =>
        record.isFallback ? (
          <CheckOutlined style={{ color: "var(--tertiary-color)" }} />
        ) : (
          <CloseOutlined style={{ color: "var(--red)" }} />
        ),
    },
    {
      id: "createdAt",
      title: "Creation Timestamp",
      dataIndex: "createdAt",
      fixed: undefined,
      width: 160,
      render: (text: string) => <TimezoneConverter date={text} />,
    },
    {
      id: "deleted",
      title: "Deleted",
      dataIndex: "deleted",
      width: 75,
      fixed: undefined,
      render: (text: string, record: any) =>
        record.deleted ? (
          <CheckOutlined style={{ color: "var(--tertiary-color)" }} />
        ) : (
          <CloseOutlined style={{ color: "var(--red)" }} />
        ),
    },
    {
      id: "actions",
      title: "Actions",
      width: 160,
      render: (_: any, record: any) => {
        const userPermissions = this.userContext.userDetails?.permissions || [];
        const canEdit = 
            checkPermissions(userPermissions, [
          {
            customerApplication: "Mercury",
            permissionName: "Invoice Types",
            action: "edit",
          },
        ]);
        const canDelete = 
            checkPermissions(userPermissions, [
          {
            customerApplication: "Mercury",
            permissionName: "Invoice Types",
            action: "delete",
          },
        ]);
        return record?.deleted ? (
          <PermissionWrapper
            hasPermissions={canEdit}
            children={
              <InvoiceTypeForm
                disabled={!canEdit}
                mode="restore"
                editData={record}
                refreshList={() => this.reload()}
              />
            }
          />
        ) : (
          <Space align="center">
            <PermissionWrapper
              hasPermissions={canEdit}
              children={
                <InvoiceTypeForm
                  mode="edit"
                  editData={record}
                  refreshList={() => this.reload()}
                  disabled={!canEdit}
                />
              }
            />
            <PermissionWrapper
              hasPermissions={canDelete}
              children={
                <InvoiceTypeForm
                  mode="delete"
                  editData={record}
                  refreshList={() => this.reload()}
                  disabled={!canDelete}
                />
              }
            />
          </Space>
        );
      },
    },
  ];

  renderActions() {
    const userPermissions = this.userContext.userDetails?.permissions || [];
    const canCreate = 
        checkPermissions(userPermissions, [
      {
        customerApplication: "Mercury",
        permissionName: "Invoice Types",
        action: "create",
      },
    ]);

    return (
      <Space size="small">
        <PermissionWrapper
          hasPermissions={canCreate}
          children={
            <InvoiceTypeForm
              disabled={!canCreate}
              refreshList={() => this.reload()}
            />
          }
        />
      </Space>
    );
  }

  rowClassName = (record: any): string => {
    return "row";
  };

  async fetch() {
    const { sortOrder, sortField } = this.state;
    const filters = this.filtersToQuery();

    const res = await this.userContext.invoicingClient.post(
      `/invoicing-v1/types/search`,
      {
        filter: {
          deleted: filters.deleted ?? false,
          ...filters,
        },
        sort: {
          [sortField || "name"]: sortOrder === "ascend" ? 1 : -1,
        },
        pagination: {
          pageNumber: this.currentPage || 1,
          resultsPerPage: this.itemsPerPage,
        },
      }
    );
    return { data: res.data } as any;
  }

  pageTitle = "Manage Invoice Types - Mercury";
  title = "Manage Invoice Types";
  pageClass = "invoiceTypesTable";
}

export default function ManageInvoiceTypes() {

  const
      context = useMercuryContext(),
      breaks = useBreakpoint();

  return (
    //@ts-ignore
    <InvoiceTypes
      id={"ManageInvoiceTypes"}
      context={context as any}
      useLegacyEndpoints={false}
      breaks={breaks as any}
      hasSidebar={true}
      noSelect
    />
  );
}
